import { IntegrationData } from 'types/mounting'
import { FilterCriteria } from '../../inputFilters'
import { RoofTypeName } from '../../types'
import { getRoofCoveringToRoofTypeMap } from './K2HelperFunctions'
import { K2Lookup } from './K2lookups'
import { K2MountingSystemName } from './K2Types'

export function generateCompatibilityRules(k2MountingSystemName: K2MountingSystemName) {
  const k2LookupFields = K2Lookup.data.US.imperial[k2MountingSystemName].fields

  // Generate rail length compatibility: railLength -> [compatible railTypes]
  const railLengthCompatibility = {}
  const railLengthMap =
    k2LookupFields?.railLength?.mapping && typeof k2LookupFields.railLength.mapping === 'object'
      ? (k2LookupFields.railLength.mapping as Record<string, string[]>)
      : {}
  Object.entries(railLengthMap).forEach(([railType, lengths]) => {
    if (!Array.isArray(lengths)) return
    lengths.forEach((length) => {
      if (!railLengthCompatibility[length]) {
        railLengthCompatibility[length] = []
      }
      railLengthCompatibility[length].push(railType)
    })
  })

  // Generate rail finish compatibility: railFinish -> [compatible railType~railLength combinations]
  const railFinishCompatibility = {}
  const railFinishMap =
    k2LookupFields?.railFinish?.mapping && typeof k2LookupFields.railFinish.mapping === 'object'
      ? (k2LookupFields.railFinish.mapping as Record<string, Record<string, string[]>>)
      : {}
  Object.entries(railFinishMap).forEach(([railType, lengthFinishes]) => {
    if (!lengthFinishes || typeof lengthFinishes !== 'object' || Array.isArray(lengthFinishes)) return
    Object.entries(lengthFinishes).forEach(([length, finishes]) => {
      if (!Array.isArray(finishes)) return
      finishes.forEach((finish) => {
        if (!railFinishCompatibility[finish]) {
          railFinishCompatibility[finish] = []
        }
        railFinishCompatibility[finish].push(`${railType}~${length}`)
      })
    })
  })

  // Generate attachments compatibility: attachmentPartNumber -> [compatible roofType~mountingOption combinations]
  const attachmentsCompatibility = {}
  const roofCoveringToRoofTypeMap = getRoofCoveringToRoofTypeMap()
  const attachmentsMap =
    k2LookupFields?.attachments?.mapping && typeof k2LookupFields.attachments.mapping === 'object'
      ? (k2LookupFields.attachments.mapping as Record<string, unknown>)
      : {}
  Object.entries(attachmentsMap).forEach(([roofCovering, mountingOptions]) => {
    const roofTypes = roofCoveringToRoofTypeMap[roofCovering] || []
    if (!mountingOptions || typeof mountingOptions !== 'object' || Array.isArray(mountingOptions)) return
    Object.entries(mountingOptions as Record<string, unknown>).forEach(([mountingOption, attachmentPartNumbers]) => {
      if (!Array.isArray(attachmentPartNumbers)) return
      ;(attachmentPartNumbers as string[]).forEach((partNumber) => {
        if (!attachmentsCompatibility[partNumber]) {
          attachmentsCompatibility[partNumber] = []
        }
        roofTypes.forEach((roofType) => {
          attachmentsCompatibility[partNumber].push(`${roofType}~${mountingOption}`)
        })
      })
    })
  })

  // Generate endClamp compatibility: endClampPartNumber -> [compatible clampFinish values]
  const endClampCompatibility = {}
  const endClampMap =
    k2LookupFields?.endClamp?.mapping && typeof k2LookupFields.endClamp.mapping === 'object'
      ? (k2LookupFields.endClamp.mapping as Record<string, string[]>)
      : {}
  Object.entries(endClampMap).forEach(([clampFinish, endClampPartNumbers]) => {
    if (!Array.isArray(endClampPartNumbers)) return
    endClampPartNumbers.forEach((partNumber) => {
      if (!endClampCompatibility[partNumber]) {
        endClampCompatibility[partNumber] = []
      }
      endClampCompatibility[partNumber].push(clampFinish)
    })
  })

  // Generate fastener compatibility: fastenerPartNumber -> [compatible roofCovering~mountingOption~attachmentPartNumber combinations]
  const fastenerCompatibility = {}
  const fastenersMap =
    k2LookupFields?.fasteners?.mapping && typeof k2LookupFields.fasteners.mapping === 'object'
      ? (k2LookupFields.fasteners.mapping as Record<string, unknown>)
      : {}
  Object.entries(fastenersMap).forEach(([roofCovering, mountingOptions]) => {
    if (!mountingOptions || typeof mountingOptions !== 'object' || Array.isArray(mountingOptions)) return
    Object.entries(mountingOptions as Record<string, unknown>).forEach(([mountingOption, attachmentFasteners]) => {
      if (!attachmentFasteners || typeof attachmentFasteners !== 'object' || Array.isArray(attachmentFasteners)) return
      Object.entries(attachmentFasteners as Record<string, unknown>).forEach(
        ([attachmentPartNumber, fastenerPartNumbers]) => {
          if (!Array.isArray(fastenerPartNumbers)) return
          ;(fastenerPartNumbers as string[]).forEach((fastenerPartNumber) => {
            if (!fastenerCompatibility[fastenerPartNumber]) {
              fastenerCompatibility[fastenerPartNumber] = []
            }
            fastenerCompatibility[fastenerPartNumber].push(`${roofCovering}~${mountingOption}~${attachmentPartNumber}`)
          })
        }
      )
    })
  })

  // Derive mountingOption compatibility from attachments: include only roofCoverings
  // where the corresponding attachments array for that mountingOption has at least one item
  type AttachmentOptions = { rafter: string[]; deck: string[] }
  const roofCoveringCompatibility = { rafter: [] as string[], deck: [] as string[] }
  const attachmentsByCoveringRaw = k2LookupFields?.attachments?.mapping
  if (
    attachmentsByCoveringRaw &&
    typeof attachmentsByCoveringRaw === 'object' &&
    !Array.isArray(attachmentsByCoveringRaw)
  ) {
    Object.entries(attachmentsByCoveringRaw as Record<string, unknown>).forEach(([covering, o]) => {
      const options = o as Partial<AttachmentOptions>
      if (Array.isArray(options.rafter) && options.rafter.length > 0) {
        roofCoveringCompatibility.rafter.push(covering)
      }
      if (Array.isArray(options.deck) && options.deck.length > 0) {
        roofCoveringCompatibility.deck.push(covering)
      }
    })
  }

  const roofShapeCompatibility = {
    k2OnRoof: ['monoslope', 'gable'],
    k2SimpleTilt: ['flat'],
  }

  return {
    railLength: { railLengthCompatibility },
    railFinish: { railFinishCompatibility },
    attachments: { attachmentsCompatibility },
    endClamp: { endClampCompatibility },
    fastener: { fastenerCompatibility },
    mountingOption: { roofCoveringCompatibility },
    roofShape: { roofShapeCompatibility },
  }
}

export function generateFilterCriteria(
  k2MountingSystemName: K2MountingSystemName,
  integrationData: IntegrationData,
  roofTypeName: RoofTypeName | null
): FilterCriteria[] {
  const railType = String(integrationData.railType || '')
  const railLength = String(integrationData.railLength || '')
  const railFinish = String(integrationData.railFinish || '')
  const mountingOption = String(integrationData.mountingOption || '')
  const clampFinish = String(integrationData.clampFinish || '')
  const attachments = String(integrationData.attachments || '')
  const roofType = roofTypeName || ''

  // Map roof type to roof covering for fastener compatibility
  const roofCoveringToRoofTypeMap = getRoofCoveringToRoofTypeMap()
  const roofCovering =
    Object.entries(roofCoveringToRoofTypeMap).find(([covering, types]) =>
      types.includes(roofType as RoofTypeName)
    )?.[0] || ''

  return [
    {
      filterType: 'railLengthCompatibility',
      filterValue: railType,
    },
    {
      filterType: 'railFinishCompatibility',
      filterValue: `${railType}~${railLength}`,
    },
    {
      filterType: 'roofCoveringCompatibility',
      filterValue: roofCovering,
    },
    {
      filterType: 'attachmentsCompatibility',
      filterValue: `${roofType}~${mountingOption}`,
    },
    {
      filterType: 'railsCompatibility',
      filterValue: `${railType}~${railLength}~${railFinish}`,
    },
    {
      filterType: 'endClampCompatibility',
      filterValue: clampFinish,
    },
    {
      filterType: 'fastenerCompatibility',
      filterValue: `${roofCovering}~${mountingOption}~${attachments}`,
    },
    {
      filterType: 'roofShapeCompatibility',
      filterValue: k2MountingSystemName,
    },
  ]
}

export const roofTypesCompatibleWithK2Crossrail: RoofTypeName[] = [
  'Tile Clay',
  'Tile Concrete',
  'Tile Slate',
  'Composition / Asphalt Shingle',
  'Metal Standing Seam',
  'Rolled Asphalt',
  'Trapezoidal',
  'Flat Concrete',
  'Membrane EPDM',
]

export const roofTypesCompatibleWithK2SimpleTilt: RoofTypeName[] = ['Flat Concrete', 'Membrane EPDM']
